import { memo, useId } from 'react';

import { InfoIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';

import { WorkspaceAgentGuidedVariantNumberUnitField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantNumberUnitField';
import {
  getWorkspaceAgentRoundedCornersMaxRadius,
  type WorkspaceAgentRoundedCornersUnitId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRoundedCorners';

const ROUNDED_CORNERS_RADIUS_MAX = {
  percent: getWorkspaceAgentRoundedCornersMaxRadius('percent'),
  px: getWorkspaceAgentRoundedCornersMaxRadius('px'),
};

export type WorkspaceAgentGuidedVariantRoundedCornersRadiusFieldProps = {
  onRadiusChange: (radius: number) => void;
  onUnitChange: (unit: WorkspaceAgentRoundedCornersUnitId) => void;
  radius: number;
  unit: WorkspaceAgentRoundedCornersUnitId;
};

export const WorkspaceAgentGuidedVariantRoundedCornersRadiusField = memo(
  ({ onRadiusChange, onUnitChange, radius, unit }: WorkspaceAgentGuidedVariantRoundedCornersRadiusFieldProps) => {
    const hintId = useId();
    const inputId = useId();

    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center gap-1">
          <label className="font-medium text-12 text-gray-12" htmlFor={inputId}>
            Corner radius
          </label>
          <Tooltip label="Percent is relative to the shorter side of each image. Pixels are a fixed radius.">
            <button
              aria-describedby={hintId}
              aria-label="About corner radius units"
              className="inline-flex items-center"
              type="button"
            >
              <InfoIcon aria-hidden className="text-gray-9" size="xs" />
            </button>
          </Tooltip>
          <span className="sr-only" id={hintId}>
            Percent is relative to the shorter side of each image. Pixels are a fixed radius.
          </span>
        </div>
        <WorkspaceAgentGuidedVariantNumberUnitField
          aria-describedby={hintId}
          id={inputId}
          max={ROUNDED_CORNERS_RADIUS_MAX}
          min={0}
          onUnitChange={onUnitChange}
          onValueChange={onRadiusChange}
          unit={unit}
          value={radius}
        />
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantRoundedCornersRadiusField.displayName =
  'WorkspaceAgentGuidedVariantRoundedCornersRadiusField';
